import { useState } from 'react';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import {
  DetectedWallet,
  useWalletSign,
} from '../../xpx-perps-withdrawal/useWalletSign';
import {
  DepositItem,
  listDeposits,
  retryDeposit,
} from '../../../services/xbitRelayService';

type Props = {
  record: DepositItem;
  onClose(): void;
};

const POLL_INTERVAL_MS = 2000;
const POLL_ATTEMPTS = 5;

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

const isUserRejection = (err: any): boolean => {
  const code = err?.code ?? err?.cause?.code;
  if (code === 4001 || code === 'ACTION_REJECTED') return true;
  const msg = (err?.message || '').toLowerCase();
  return msg.includes('reject') || msg.includes('denied');
};

const buildMessage = (row: DepositItem, timestamp: number) =>
  `ChainID: ${row.chainId}\n` +
  `RawAmount: ${row.rawAmount}\n` +
  `TxHash: ${row.txHash}\n` +
  `UserAddress: ${row.targetAddress}\n` +
  `DepositAddress: ${row.toAddress}\n` +
  `Timestamp: ${timestamp}`;

export function RetryDialog({ record, onClose }: Props) {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { detectedWallets, connect, address, signMessage } = useWalletSign();
  const [selectedWallet, setSelectedWallet] = useState<DetectedWallet | null>(
    null,
  );
  const [connecting, setConnecting] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConnect = async () => {
    if (!selectedWallet) return;
    setConnecting(true);
    try {
      await connect(selectedWallet.provider);
    } catch (err: any) {
      if (!isUserRejection(err)) {
        notify(err?.message || t('common.error'), { type: 'error' });
      }
    } finally {
      setConnecting(false);
    }
  };

  const pollFinalState = async (
    originalErrorMsg: string,
    attempt = 0,
  ): Promise<void> => {
    if (attempt >= POLL_ATTEMPTS) {
      notify(t('module.xbitRelayDeposits.retryPending'), { type: 'info' });
      return;
    }
    await wait(POLL_INTERVAL_MS);
    try {
      const res = await listDeposits({
        txHash: record.txHash,
        provider: record.provider,
        pageSize: 1,
      });
      const fresh = res.data?.items?.[0];
      if (fresh?.status === 'SUCCESS' || fresh?.status === 'CONFIRMING') {
        notify(
          t('module.xbitRelayDeposits.retrySuccess', { status: fresh.status }),
          { type: 'success' },
        );
        return;
      }
      if (
        fresh?.status === 'FAILED' &&
        fresh.errorMsg &&
        fresh.errorMsg !== originalErrorMsg
      ) {
        notify(fresh.errorMsg, { type: 'error' });
        return;
      }
    } catch {
      // ignore poll errors, retry next tick
    }
    await pollFinalState(originalErrorMsg, attempt + 1);
  };

  const handleSignAndRetry = async () => {
    if (!address) return;
    setLoading(true);
    try {
      const timestamp = Date.now();
      const message = buildMessage(record, timestamp);
      const signature = await signMessage(message);
      const { data } = await retryDeposit(record.id, {
        admin_id: address.toLowerCase(),
        signature,
        timestamp,
      });

      if (!data.status) {
        notify(data.message || t('module.xbitRelayDeposits.retryRejected'), {
          type: 'error',
        });
        return;
      }

      notify(t('module.xbitRelayDeposits.retryQueued'), { type: 'info' });
      onClose();
      await pollFinalState(record.errorMsg || '');
      refresh();
    } catch (err: any) {
      if (isUserRejection(err)) return;
      notify(
        err?.response?.data?.message || err?.message || t('common.error'),
        { type: 'error' },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('module.xbitRelayDeposits.retryTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <Typography variant="body2">
            {t('module.xbitRelayDeposits.retryConfirm')}
          </Typography>

          <Stack spacing={0.5}>
            <Typography variant="body2">
              <b>{t('module.xbitRelayDeposits.chain')}:</b> {record.chainId}
            </Typography>
            <Typography variant="body2">
              <b>{t('module.xbitRelayDeposits.token')}:</b>{' '}
              {record.tokenSymbol || record.token}
            </Typography>
            <Typography variant="body2">
              <b>{t('module.xbitRelayDeposits.amount')}:</b> {record.amount}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              <b>{t('module.xbitRelayDeposits.txHash')}:</b> {record.txHash}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              <b>{t('module.xbitRelayDeposits.targetAddress')}:</b>{' '}
              {record.targetAddress}
            </Typography>
          </Stack>

          {record.errorMsg && (
            <Typography variant="caption" color="error" sx={{ whiteSpace: 'pre-line' }}>
              <b>{t('module.xbitRelayDeposits.previousError')}:</b>{' '}
              {record.errorMsg}
            </Typography>
          )}

          {detectedWallets.length === 0 ? (
            <Typography variant="body2" color="error">
              {t('module.xbitRelayDeposits.noWalletFound')}
            </Typography>
          ) : (
            <FormControl size="small" fullWidth>
              <InputLabel>
                {t('module.xbitRelayDeposits.selectWallet')}
              </InputLabel>
              <Select
                label={t('module.xbitRelayDeposits.selectWallet')}
                value={selectedWallet?.name ?? ''}
                onChange={(e) => {
                  const w = detectedWallets.find(
                    (x) => x.name === e.target.value,
                  );
                  setSelectedWallet(w ?? null);
                }}
                disabled={Boolean(address) || connecting || loading}
              >
                {detectedWallets.map((w) => (
                  <MenuItem key={w.name} value={w.name}>
                    {w.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {!address && selectedWallet && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleConnect}
              disabled={connecting}
              startIcon={connecting ? <CircularProgress size={14} /> : null}
            >
              {t('module.xbitRelayDeposits.connect')}
            </Button>
          )}

          {address && (
            <Typography variant="caption" color="text.secondary">
              {t('module.xbitRelayDeposits.connectedAs')}: {address}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          disabled={loading || connecting}
        >
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSignAndRetry}
          disabled={!address || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {t('module.xbitRelayDeposits.signAndRetry')}
        </Button>
      </DialogActions>
    </>
  );
}
